import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, CheckCircle2, Loader2, Repeat } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { toast } from "sonner";
import { tr } from "../lib/i18n";
import { cn } from "../lib/utils";
import { PublicLanguageToggle } from "../components/app/PublicLanguageToggle";
import { ServiceDetailsFields, getRequiredServiceFieldKeys } from "../components/app/ServiceDetailsFields";
import { PublicBusinessHero } from "../components/public/PublicBusinessHero";
import { PublicBusinessAbout } from "../components/public/PublicBusinessAbout";
import { PublicBusinessServices } from "../components/public/PublicBusinessServices";
import { PublicBusinessGallery } from "../components/public/PublicBusinessGallery";
import { PublicBusinessHighlights } from "../components/public/PublicBusinessHighlights";
import { PublicBusinessReviews } from "../components/public/PublicBusinessReviews";
import { PublicQuoteCTA } from "../components/public/PublicQuoteCTA";
import { applyBookingPageMeta } from "../lib/bookingPageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// US states + DC + common territories. The order is alphabetical so the
// dropdown is fast to scan. We surface only the 2-letter code in the
// `<option>` text — the label/aria-label already says "State" so the
// abbreviation is unambiguous and stays mobile-friendly.
const US_STATES = [
  { code: "AL" }, { code: "AK" }, { code: "AZ" }, { code: "AR" },
  { code: "CA" }, { code: "CO" }, { code: "CT" }, { code: "DE" },
  { code: "DC" }, { code: "FL" }, { code: "GA" }, { code: "HI" },
  { code: "ID" }, { code: "IL" }, { code: "IN" }, { code: "IA" },
  { code: "KS" }, { code: "KY" }, { code: "LA" }, { code: "ME" },
  { code: "MD" }, { code: "MA" }, { code: "MI" }, { code: "MN" },
  { code: "MS" }, { code: "MO" }, { code: "MT" }, { code: "NE" },
  { code: "NV" }, { code: "NH" }, { code: "NJ" }, { code: "NM" },
  { code: "NY" }, { code: "NC" }, { code: "ND" }, { code: "OH" },
  { code: "OK" }, { code: "OR" }, { code: "PA" }, { code: "RI" },
  { code: "SC" }, { code: "SD" }, { code: "TN" }, { code: "TX" },
  { code: "UT" }, { code: "VT" }, { code: "VA" }, { code: "WA" },
  { code: "WV" }, { code: "WI" }, { code: "WY" },
  { code: "PR" }, { code: "VI" }, { code: "GU" },
];

// Service types are short brand-style nouns. We keep the canonical English
// value as the option `value` (so the saved lead has a consistent service_type
// across languages and the AI quote prompt's pricing reference still matches),
// and only translate the visible label.
const SERVICE_TYPES = [
  { value: "Standard Clean",        en: "Standard Clean",         es: "Limpieza estándar" },
  { value: "Deep Clean",            en: "Deep Clean",             es: "Limpieza profunda" },
  { value: "Move-Out",              en: "Move-Out",               es: "Mudanza (salida)" },
  { value: "Move-In",               en: "Move-In",                es: "Mudanza (entrada)" },
  { value: "Post-Construction",     en: "Post-Construction",      es: "Post-construcción" },
  { value: "Airbnb Turnover",       en: "Airbnb Turnover",        es: "Rotación Airbnb" },
  { value: "Office / Commercial",   en: "Office / Commercial",    es: "Oficina / Comercial" },
];

export default function PublicLeadForm() {
  const { ownerId } = useParams();
  const [searchParams] = useSearchParams();
  // Returning-customer attribution from the Rebook Reminder email's CTA.
  // The server still validates `previous_lead_id` ownership; URLs the
  // customer can edit are never trusted blind.
  const isReturningCustomer = searchParams.get("returning_customer") === "true";
  // Find a Cleaner referral hint (allow-listed; server re-validates).
  const discoverySource =
    searchParams.get("src") === "find_a_cleaner" ? "find_a_cleaner" : null;
  const previousLeadId = searchParams.get("previous_lead_id") || "";
  const prefilledName = searchParams.get("customer_name") || "";
  const prefilledEmail = searchParams.get("customer_email") || "";
  const prefilledPhone = searchParams.get("customer_phone") || "";

  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  // PUBLIC-page only language. Defaults to English; intentionally NOT
  // persisted to localStorage so a customer's choice never overwrites the
  // owner's app-language preference. We do try to seed from the browser's
  // first nav language if it starts with "es" — a small UX win for native
  // Spanish speakers — but the toggle is always visible to override.
  const [lang, setLang] = useState(() => {
    try {
      // Explicit, allow-listed ?lang= (e.g. from Find a Cleaner) wins;
      // otherwise keep the existing browser-language fallback.
      const qsLang = new URLSearchParams(window.location.search).get("lang");
      if (qsLang === "es" || qsLang === "en") return qsLang;
      const navLang = (navigator?.language || "").toLowerCase();
      return navLang.startsWith("es") ? "es" : "en";
    } catch {
      return "en";
    }
  });
  const t = (key, ...args) => tr(key, lang, ...args);

  const [form, setForm] = useState({
    name: prefilledName, email: prefilledEmail, phone: prefilledPhone,
    // Structured US-style address. The legacy single-line `address` is
    // still derived server-side from these five fields so all existing
    // display surfaces (lead detail, calendar modal, quote page) keep
    // reading `lead.address` unchanged.
    street_address: "", address_line_2: "", city: "", state: "", zip_code: "",
    service_type: "", preferred_date: "", message: "",
  });
  // Smart conditional fields per service type (bedrooms, bathrooms, sqft,
  // frequency, pets, etc.). Kept separate from `form` so we can reset
  // cleanly when the service type changes without losing core fields.
  const [serviceDetails, setServiceDetails] = useState({});
  // Inline validation errors keyed by field name (e.g. "phone",
  // "service_type", "sms_consent", "sd.bedrooms"). When non-empty, the
  // matching field renders a red border and a small red message below it.
  // The submit handler scrolls to the FIRST field in canonical order on
  // failure so the customer always sees the problem without hunting.
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const setField = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    // Clear this field's inline error the moment the user edits it so
    // the form feels responsive — they don't have to re-submit just to
    // see the red border disappear.
    setErrors((prev) => {
      if (!prev[k]) return prev;
      const { [k]: _drop, ...rest } = prev;
      return rest;
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/business/${ownerId}`);
        setBusiness(data);
      } catch {
        setErr("invalid");
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerId]);

  // Business-specific social/SEO metadata (additive — see bookingPageMeta.js
  // for the crawler-visibility reality). Skipped for invalid links so no
  // metadata leaks for nonexistent owners.
  useEffect(() => {
    if (!business) return undefined;
    return applyBookingPageMeta({
      business,
      ownerId,
      backendUrl: process.env.REACT_APP_BACKEND_URL,
    });
  }, [business, ownerId]);

  // ---- Privacy-first funnel analytics (best-effort, additive) ----
  // Owner previews carry ?preview=1 (Dashboard "Open Page" / Settings
  // "Preview Page") and are never counted. sessionStorage dedupes per tab
  // session so refresh spam / re-renders don't inflate counts. Failures are
  // swallowed — analytics can never affect the page or the form.
  const isPreview = new URLSearchParams(window.location.search).get("preview") === "1";
  const trackEvent = (event, storageKey) => {
    if (isPreview) return;
    try {
      if (sessionStorage.getItem(storageKey)) return;
      sessionStorage.setItem(storageKey, "1");
    } catch {
      // storage unavailable (private mode) — still fire once per mount.
    }
    fetch(`${API}/public/booking-analytics/${ownerId}/${event}`, { method: "POST" }).catch(() => {});
  };

  useEffect(() => {
    if (!business) return;
    trackEvent("view", `tasklio_bp_view_${ownerId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, ownerId]);

  const quoteStartSent = useRef(false);
  const handleQuoteStartCapture = () => {
    if (quoteStartSent.current) return;
    quoteStartSent.current = true;
    trackEvent("start", `tasklio_bp_start_${ownerId}`);
  };

  // Wrap setServiceDetails so any inline error for a smart-field clears
  // the instant its value changes — same "responsive" pattern as the
  // core fields above.
  const setServiceDetailsWithClear = (updater) => {
    setServiceDetails((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const changedKeys = new Set([
        ...Object.keys(prev || {}),
        ...Object.keys(next || {}),
      ]);
      setErrors((errs) => {
        let mutated = false;
        const out = { ...errs };
        for (const k of changedKeys) {
          const errKey = `sd.${k}`;
          if (out[errKey] !== undefined) {
            if ((prev || {})[k] !== (next || {})[k]) {
              delete out[errKey];
              mutated = true;
            }
          }
        }
        return mutated ? out : errs;
      });
      return next;
    });
  };

  // Canonical field order — used to decide which inline error to scroll
  // to first on a failed submit. Keep this in sync with the visual order
  // of the form below so customers always see the topmost problem.
  const fieldOrder = (sdKeys) => [
    "name",
    "phone",
    "street_address",
    "city",
    "state",
    "zip_code",
    "service_type",
    ...sdKeys.map((k) => `sd.${k}`),
  ];

  const validate = () => {
    const e = {};
    const name = (form.name || "").trim();
    const street = (form.street_address || "").trim();
    const city = (form.city || "").trim();
    const state = (form.state || "").trim();
    const zip = (form.zip_code || "").trim();
    const service = (form.service_type || "").trim();
    if (!name) e.name = t("public.form.err.name");
    // Phone is optional (the cleaning business contacts the customer
    // using whichever channel the customer chose to share). Tasklio AI
    // does not send SMS to customers — the consent checkbox was removed
    // for A2P 10DLC compliance accuracy.
    if (!street) e.street_address = t("public.form.err.streetAddress");
    if (!city) e.city = t("public.form.err.city");
    if (!state) e.state = t("public.form.err.state");
    if (!zip) e.zip_code = t("public.form.err.zip");
    else if (!/^\d{5}(-\d{4})?$/.test(zip)) e.zip_code = t("public.form.err.zipInvalid");
    if (!service) e.service_type = t("public.form.err.serviceType");
    // Per-field smart-detail validation — every field rendered for the
    // selected service type is required. `false` (a valid Yes/No answer)
    // is NOT treated as missing.
    if (service) {
      const requiredSdKeys = getRequiredServiceFieldKeys(service);
      for (const k of requiredSdKeys) {
        const v = serviceDetails[k];
        if (v === undefined || v === null || v === "") {
          // Use a field-specific message when one exists, otherwise the
          // generic "This field is required." fallback.
          const specific = t(`public.form.err.sd.${k}`);
          const fallback = t("public.form.err.sd.generic");
          // tr() returns the key itself when missing — detect that and
          // fall through to the generic message so callers never see
          // raw i18n keys leak into the UI.
          e[`sd.${k}`] = specific && !specific.endsWith(`.${k}`) ? specific : fallback;
        }
      }
    }
    return e;
  };

  // Scroll to the first inline error and focus it for keyboard users.
  const scrollToFirstError = (errMap) => {
    if (!formRef.current) return;
    const order = fieldOrder(getRequiredServiceFieldKeys(form.service_type));
    const firstKey = order.find((k) => Boolean(errMap[k]));
    if (!firstKey) return;
    const root = formRef.current;
    // Locate the wrapper that owns this field via the `data-field` attr.
    const el = root.querySelector(`[data-field="${CSS.escape(firstKey)}"]`);
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {
      el.scrollIntoView();
    }
    // Focus the first focusable element inside (input / select / button).
    // Use a small timeout so the scroll animation doesn't fight the focus.
    setTimeout(() => {
      const focusable = el.querySelector(
        'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable && typeof focusable.focus === "function") {
        try { focusable.focus({ preventScroll: true }); } catch { focusable.focus(); }
      }
    }, 250);
  };

  // Anti-spam honeypot (hidden input, uncontrolled — never touches form state)
  const honeypotRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    const errMap = validate();
    if (Object.keys(errMap).length > 0) {
      setErrors(errMap);
      // Defer scroll one tick so the inline errors render first and the
      // wrapper height accounts for them. Avoids a "jump" past the field.
      requestAnimationFrame(() => scrollToFirstError(errMap));
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      // Attach smart conditional fields. Filter empty values so the
      // backend doesn't store keys the customer didn't actually answer.
      const sd = Object.fromEntries(
        Object.entries(serviceDetails).filter(
          ([, v]) => v !== "" && v !== undefined && v !== null
        )
      );
      if (Object.keys(sd).length > 0) {
        payload.service_details = sd;
      }
      // Forward returning-customer attribution from the URL so the backend
      // can mark this as a `returning_customer` lead and link it to the
      // previous booking. The backend validates `previous_lead_id` against
      // the owner_id — anyone tampering with the URL just loses the link,
      // they can't spoof another business.
      if (isReturningCustomer) {
        payload.returning_customer = true;
        if (previousLeadId) payload.previous_lead_id = previousLeadId;
      }
      // Anti-spam honeypot: forwarded only if a bot filled the hidden field.
      if (honeypotRef.current?.value) payload.website = honeypotRef.current.value;
      if (discoverySource) payload.discovery_source = discoverySource;
      await axios.post(`${API}/public/leads/${ownerId}`, payload);
      setSubmitted(true);
    } catch (error) {
      if (error?.response?.status === 429) {
        toast.error(t("public.form.tooManyRequests"));
      } else {
        const detail = error?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : t("public.form.submitError"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Mini-site wrapper (additive; the quote form itself is untouched).
  // Ref wraps the existing form card so the hero / CTA buttons can smooth-
  // scroll customers straight to the quote form on the same page.
  const quoteFormRef = useRef(null);
  const scrollToQuoteForm = () => {
    if (!quoteFormRef.current) return;
    try {
      quoteFormRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch {
      quoteFormRef.current.scrollIntoView();
    }
  };
  // Per-business service filtering. None/absent/empty enabled_services =
  // ALL services (legacy behavior). Unknown stored values are ignored; if
  // filtering would somehow leave zero valid options (data corruption
  // safeguard), fall back to the full list so the form never breaks.
  // This same list drives BOTH the quote dropdown and the public Services
  // marketing section, so they always match.
  const enabledServices =
    Array.isArray(business?.enabled_services) && business.enabled_services.length > 0
      ? new Set(business.enabled_services)
      : null;
  const filteredServiceTypes = enabledServices
    ? SERVICE_TYPES.filter((s) => enabledServices.has(s.value))
    : SERVICE_TYPES;
  const availableServiceTypes =
    filteredServiceTypes.length > 0 ? filteredServiceTypes : SERVICE_TYPES;

  // Owner-entered bilingual content. ES shows the owner's Spanish copy when
  // present; otherwise falls back to the English/default text. EN always
  // shows the English/default text. Never renders blank when EN exists.
  const esOr = (esVal, enVal) =>
    lang === "es" && (esVal || "").trim() ? esVal.trim() : (enVal || "");
  const displayTagline = esOr(business?.booking_tagline_es, business?.booking_tagline);
  const displayAbout = esOr(business?.booking_about_es, business?.booking_about);
  const displayServiceArea = esOr(
    business?.booking_service_area_es,
    business?.booking_service_area
  );

  // The CTA band only renders when at least one optional content section
  // sits between the hero and the form — legacy uncustomized pages stay
  // compact (hero + form only).
  const hasMiniSiteSections = Boolean(
    business &&
      (((business.booking_about || "").trim()) ||
        (business.booking_highlights || []).length > 0 ||
        (business.booking_gallery_images || []).length > 0 ||
        availableServiceTypes.length > 0 ||
        (business.google_rating != null && business.google_review_count != null))
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9f8]">
        <Loader2 className="w-6 h-6 animate-spin text-green-800" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f9f9f8] p-6" data-testid="public-form-invalid">
        <div className="max-w-md text-center">
          <p className="font-display text-2xl font-semibold text-gray-900">{t("public.form.invalidTitle")}</p>
          <p className="mt-2 text-gray-500">{t("public.form.invalidBody")}</p>
          <Link to="/" className="mt-6 inline-block text-green-800 hover:underline">{t("public.form.gotoTasklio")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f9f8]" data-testid="public-form-page">
      <div className="max-w-2xl lg:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="flex items-center justify-between mb-8 gap-3">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-lg">Tasklio AI</span>
          </div>
          <PublicLanguageToggle lang={lang} setLang={setLang} testidPrefix="public-form-lang-switch" />
        </div>

        {submitted ? (
          <div className="lg:max-w-2xl lg:mx-auto">
          <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center" data-testid="public-form-success">
            <div className="w-14 h-14 rounded-full bg-green-100 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-green-800" />
            </div>
            <h1 className="font-display mt-5 text-3xl font-semibold text-gray-900">
              {t("public.form.thanks", form.name)}
            </h1>
            <p className="mt-3 text-gray-600">
              {t("public.form.thanksBody", business.business_name)}
            </p>
          </div>
          </div>
        ) : (
          <>
            {/* ---- Business mini-site (additive, all sections above the
                PROTECTED quote form). Optional sections disappear entirely
                when the owner hasn't provided their data. ---- */}
            <PublicBusinessHero
              business={business}
              t={t}
              onGetQuote={scrollToQuoteForm}
              tagline={displayTagline}
              serviceArea={displayServiceArea}
            />
            <PublicBusinessAbout about={displayAbout} t={t} />
            <PublicBusinessServices services={availableServiceTypes} lang={lang} t={t} />
            <PublicBusinessGallery images={business.booking_gallery_images} t={t} />
            <PublicBusinessHighlights highlights={business.booking_highlights} t={t} />
            <PublicBusinessReviews business={business} t={t} />
            {hasMiniSiteSections && <PublicQuoteCTA t={t} onGetQuote={scrollToQuoteForm} />}
            {/* Outer wrapper only — the form card below is the PROTECTED
                Twilio-approved quote form and must remain unchanged. */}
            <div
              ref={quoteFormRef}
              className="scroll-mt-6 lg:max-w-2xl lg:mx-auto"
              data-testid="public-quote-form-anchor"
              onFocusCapture={handleQuoteStartCapture}
            >
          <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-10">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-green-800">{t("public.form.eyebrow")}</span>
            <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight">
              {t("public.form.title", business.business_name)}
            </h1>
            <p className="mt-3 text-gray-500">{t("public.form.subtitle")}</p>

            {isReturningCustomer && (
              <div
                className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-md border border-green-200 bg-green-50 text-green-800"
                data-testid="public-form-returning-banner"
              >
                <Repeat className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">
                  {t("public.form.welcomeBack")}
                </span>
              </div>
            )}

            <form ref={formRef} noValidate onSubmit={submit} className="mt-8 space-y-5">
              {/* Honeypot: invisible to users + screen readers, ignored by tab
                  navigation. Bots that auto-fill it are silently discarded. */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                <label htmlFor="website-hp">Website</label>
                <input
                  ref={honeypotRef}
                  type="text"
                  id="website-hp"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              <div data-field="name">
                <Label htmlFor="public-name">
                  {t("public.form.name")} <span className="text-red-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="public-name"
                  value={form.name}
                  onChange={setField("name")}
                  className={cn(
                    "mt-1.5 h-11",
                    errors.name && "border-red-500 focus-visible:ring-red-500"
                  )}
                  aria-invalid={errors.name ? "true" : undefined}
                  data-testid="public-name-input"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600" data-testid="public-name-error">{errors.name}</p>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="public-email">{t("public.form.email")}</Label>
                  <Input
                    id="public-email"
                    type="email"
                    value={form.email}
                    onChange={setField("email")}
                    className="mt-1.5 h-11"
                    data-testid="public-email-input"
                  />
                </div>
                <div data-field="phone">
                  <Label htmlFor="public-phone">
                    {t("public.form.phone")}
                  </Label>
                  <Input
                    id="public-phone"
                    value={form.phone}
                    onChange={setField("phone")}
                    className={cn(
                      "mt-1.5 h-11",
                      errors.phone && "border-red-500 focus-visible:ring-red-500"
                    )}
                    aria-invalid={errors.phone ? "true" : undefined}
                    data-testid="public-phone-input"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-red-600" data-testid="public-phone-error">{errors.phone}</p>
                  )}
                </div>
              </div>

              <div className="space-y-3" data-testid="public-address-section">
                <div data-field="street_address">
                  <Label htmlFor="street-address" className="text-sm">
                    {t("public.form.streetAddress")} <span className="text-red-500" aria-hidden="true">*</span>
                  </Label>
                  <Input
                    id="street-address"
                    value={form.street_address}
                    onChange={setField("street_address")}
                    autoComplete="address-line1"
                    placeholder={t("public.form.streetAddressPlaceholder")}
                    className={cn(
                      "mt-1.5 h-11",
                      errors.street_address && "border-red-500 focus-visible:ring-red-500"
                    )}
                    aria-invalid={errors.street_address ? "true" : undefined}
                    data-testid="public-street-address-input"
                  />
                  {errors.street_address && (
                    <p className="mt-1 text-sm text-red-600" data-testid="public-street-address-error">{errors.street_address}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="address-line-2" className="text-sm">
                    {t("public.form.addressLine2")}
                  </Label>
                  <Input
                    id="address-line-2"
                    value={form.address_line_2}
                    onChange={setField("address_line_2")}
                    autoComplete="address-line2"
                    placeholder={t("public.form.addressLine2Placeholder")}
                    className="mt-1.5 h-11"
                    data-testid="public-address-line2-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_140px] gap-3">
                  <div data-field="city">
                    <Label htmlFor="city" className="text-sm">
                      {t("public.form.city")} <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="city"
                      value={form.city}
                      onChange={setField("city")}
                      autoComplete="address-level2"
                      placeholder={t("public.form.cityPlaceholder")}
                      className={cn(
                        "mt-1.5 h-11",
                        errors.city && "border-red-500 focus-visible:ring-red-500"
                      )}
                      aria-invalid={errors.city ? "true" : undefined}
                      data-testid="public-city-input"
                    />
                    {errors.city && (
                      <p className="mt-1 text-sm text-red-600" data-testid="public-city-error">{errors.city}</p>
                    )}
                  </div>
                  <div data-field="state">
                    <Label htmlFor="state" className="text-sm">
                      {t("public.form.state")} <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <select
                      id="state"
                      value={form.state}
                      onChange={(e) => {
                        const value = e.target.value;
                        setForm((f) => ({ ...f, state: value }));
                        setErrors((prev) => {
                          if (!prev.state) return prev;
                          const { state: _drop, ...rest } = prev;
                          return rest;
                        });
                      }}
                      autoComplete="address-level1"
                      aria-invalid={errors.state ? "true" : undefined}
                      className={cn(
                        "mt-1.5 h-11 w-full rounded-md border bg-white px-3 text-sm focus:outline-none focus:ring-2",
                        errors.state
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-200 focus:ring-green-700"
                      )}
                      data-testid="public-state-select"
                    >
                      <option value="">{t("public.form.statePlaceholder")}</option>
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.code}
                        </option>
                      ))}
                    </select>
                    {errors.state && (
                      <p className="mt-1 text-sm text-red-600" data-testid="public-state-error">{errors.state}</p>
                    )}
                  </div>
                  <div data-field="zip_code">
                    <Label htmlFor="zip-code" className="text-sm">
                      {t("public.form.zipCode")} <span className="text-red-500" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="zip-code"
                      value={form.zip_code}
                      onChange={setField("zip_code")}
                      inputMode="numeric"
                      maxLength={10}
                      autoComplete="postal-code"
                      placeholder={t("public.form.zipCodePlaceholder")}
                      className={cn(
                        "mt-1.5 h-11",
                        errors.zip_code && "border-red-500 focus-visible:ring-red-500"
                      )}
                      aria-invalid={errors.zip_code ? "true" : undefined}
                      data-testid="public-zip-input"
                    />
                    {errors.zip_code && (
                      <p className="mt-1 text-sm text-red-600" data-testid="public-zip-error">{errors.zip_code}</p>
                    )}
                  </div>
                </div>
              </div>
              <div data-field="service_type">
                <Label htmlFor="public-service-trigger">
                  {t("public.form.service")} <span className="text-red-500" aria-hidden="true">*</span>
                </Label>
                <Select
                  value={form.service_type}
                  onValueChange={(v) => {
                    // Reset smart-field state on service-type change so
                    // stale answers (e.g. construction_type from a prior
                    // Post-Construction selection) don't get submitted
                    // alongside a now-unrelated service type.
                    setForm((f) => ({ ...f, service_type: v }));
                    setServiceDetails({});
                    setErrors((prev) => {
                      // Clear the service_type error AND any leftover
                      // smart-field errors (they belonged to the prior
                      // service type's sheet).
                      const out = {};
                      for (const k of Object.keys(prev)) {
                        if (k === "service_type" || k.startsWith("sd.")) continue;
                        out[k] = prev[k];
                      }
                      return out;
                    });
                  }}
                >
                  <SelectTrigger
                    id="public-service-trigger"
                    className={cn(
                      "mt-1.5 h-11",
                      errors.service_type && "border-red-500 focus:ring-red-500"
                    )}
                    aria-invalid={errors.service_type ? "true" : undefined}
                    data-testid="public-service-select"
                  >
                    <SelectValue placeholder={t("public.form.servicePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableServiceTypes.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s[lang] || s.en}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.service_type && (
                  <p className="mt-1 text-sm text-red-600" data-testid="public-service-error">{errors.service_type}</p>
                )}
              </div>
              <ServiceDetailsFields
                serviceType={form.service_type}
                details={serviceDetails}
                setDetails={setServiceDetailsWithClear}
                t={t}
                errors={errors}
              />
              <div>
                <Label htmlFor="public-date">{t("public.form.preferredDate")}</Label>
                <Input
                  id="public-date"
                  type="date"
                  value={form.preferred_date}
                  onChange={setField("preferred_date")}
                  className="mt-1.5 h-11"
                  data-testid="public-date-input"
                />
              </div>
              <div>
                <Label htmlFor="public-message">{t("public.form.notes")}</Label>
                <Textarea
                  id="public-message"
                  rows={4}
                  value={form.message}
                  onChange={setField("message")}
                  className="mt-1.5"
                  placeholder={t("public.form.notesPlaceholder")}
                  data-testid="public-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-green-800 hover:bg-green-900 text-white text-base"
                data-testid="public-submit-button"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("public.form.submit")}
              </Button>
              {/* Legal compliance line under the submit CTA. Centered,
                  small gray text. Privacy + Terms open in a new tab so
                  the in-progress form doesn't lose the customer's
                  unsaved input. */}
              <p
                className="text-xs leading-relaxed text-gray-500 text-center"
                data-testid="public-legal-agreement"
              >
                {t("public.form.legalAgreement")}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-800 hover:underline font-medium"
                  data-testid="public-legal-privacy-link"
                >
                  {t("public.form.privacyPolicyLink")}
                </Link>
                {t("public.form.legalAgreementAnd")}
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-800 hover:underline font-medium"
                  data-testid="public-legal-terms-link"
                >
                  {t("public.form.termsOfServiceLink")}
                </Link>
                .
              </p>
              {/* A2P 10DLC reviewer-facing disclosure: a short, campaign-
                  aligned statement directly under the submit button
                  affirming that Tasklio AI does NOT send SMS to customers
                  through this form. The cleaning business will contact the
                  customer directly using whichever channel the customer
                  shared (phone, email, etc.). The customer SMS consent
                  checkbox was removed for compliance accuracy. */}
              <p
                className="text-[12px] leading-relaxed text-gray-500 text-center"
                data-testid="public-sms-disclosure"
              >
                {t("public.form.smsDisclosure")}
              </p>
              <p className="text-xs text-gray-400 text-center">
                {t("public.form.poweredBy")} <span className="font-medium text-gray-500">Tasklio AI</span>
              </p>
            </form>
          </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
